// EDIFACT Validator - Fehler & Struktur prüfen
import React, { useRef, useState } from "react";
import { LuCircleAlert, LuCircleCheck, LuCircleX, LuTriangleAlert, LuInfo, LuLightbulb, LuRotateCcw } from "react-icons/lu";
import PrivacyPopup from "../components/PrivacyPopup";
import SeoHead from "../components/SeoHead";
import { pageMeta } from "../data/pageMeta";
import { ToolHero, Dropzone, useResultScroll } from "../components/tools";
import { getSeparators, splitSegments, splitElements, splitComponents, readEdifactFile } from "../utils/edifact";

const MAX_BYTES = 5 * 1024 * 1024;
// Code-Ansicht: bei sehr großen Dateien nur die ersten Zeilen darstellen
const CODE_VIEW_LIMIT = 3000;

// Segment-Tags des UN/EDIFACT-Verzeichnisses inkl. Service-Segmente (UNA, UNB, UNG, UCI …)
const KNOWN_SEGMENTS = new Set((
  "UNA UNB UNG UNH UNS UNT UNE UNZ UNO UNP UNQ UNR UCI UCM UCS UCD UCF UGH UGT UIB UIH UIT UIZ USA USB USC USD USE USF USH USR UST USU USX USY " +
  "ADR AGR AJT ALC ALI APP APR ARD ARR ASD ASI ATT AUT BAS BGM BII BUS CAV CCD CCI CDI CDS CDV CED CIN CLA CLI CMP CNI CNT COD COM COT CPI CPS CPT CST CTA CUX " +
  "DAM DFN DGS DII DIM GIS DLI DLM DMS DOC DRD DSG DSI DTM EDT EFI ELM ELU ELV EMP EQA EQD EQN ERC ERP EVE FCA FII FNS FNT FOR FSQ FTX GDS GEI GID GIN GIR GOR GPO GRU " +
  "HAN HYN ICD IDE IFD IHC IMD IND INP INV IRQ LAN LIN LOC MEA MEM MKS MOA MSG MTD NAD NAT PAC PAI PAS PAT PCC PCD PCI PDI PER PGI PIA PNA POC PRC PRI PRV PSD PTY PYT " +
  "QRS QTY QUA QVR RCS REL RFF RJL RNG ROD RSL RTE SAL SCC SCD SEG SEL SEQ SFI SGP SGU SPR SPS STA STC STG STS TAX TCC TDT TEM TMD TMP TOD TPL TRU TSR VLI"
).split(" "));
// Nachrichten ohne BGM-Segment
const NO_BGM_MESSAGES = ["CONTRL", "AUTACK", "KEYMAN"];
// Beträge: Punkt oder Komma als Dezimalzeichen (beides ist nach ISO 9735 zulässig)
const NUMBER = /^-?\d+([.,]\d+)?$/;

export default function Validator() {
  const [validationResult, setValidationResult] = useState(null);
  const [edifactText, setEdifactText] = useState("");
  const [error, setError] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [highlightedSegment, setHighlightedSegment] = useState(null);
  const fileInputRef = useRef();
  const [loadedFile, setLoadedFile] = useState(null); // neue Datei → zum Ergebnis scrollen
  const resultRef = useResultScroll(loadedFile);

  // Professionelle EDIFACT-Validierung nach Standard-Regeln
  const validateEdifact = (txt) => {
    const errors = [];
    const warnings = [];
    const infos = [];
    
    if (!txt || typeof txt !== 'string') {
      errors.push({
        type: 'error',
        title: 'V-001: Ungültige Eingabe',
        segment: '-',
        line: 0,
        message: 'Keine gültige Eingabe erkannt.',
        tip: 'Bitte lade eine .edi oder .txt Datei hoch.'
      });
      return { errors, warnings, infos, isValid: false };
    }

    // V-011: Grundlänge & Zeichensatz prüfen
    const nonPrintableCount = (txt.match(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\uFFFD]/g) || []).length;
    if (nonPrintableCount > txt.length * 0.1) {
      errors.push({
        type: 'error',
        title: 'V-011: Ungültiger Zeichensatz',
        segment: '-',
        line: 0,
        message: `Zu viele nicht-druckbare Zeichen gefunden (${nonPrintableCount}).`,
        tip: 'Die Datei könnte beschädigt oder im falschen Format sein.'
      });
    }

    // V-001: EDIFACT-Struktur vorhanden
    const syntax = getSeparators(txt);
    if (!txt.includes(syntax.segment) || !txt.includes(syntax.element)) {
      errors.push({
        type: 'error',
        title: 'V-001: Keine EDIFACT-Struktur erkannt',
        segment: '-',
        line: 0,
        message: 'Datei enthält keine EDIFACT-Trennzeichen (\' und +).',
        tip: 'Dies ist keine gültige EDIFACT-Datei.'
      });
      return { errors, warnings, infos, isValid: false };
    }

    const sep = getSeparators(txt);
    const segments = splitSegments(txt, sep);
    
    // Segment-Map für schnellen Zugriff erstellen
    const segmentMap = {};
    segments.forEach((seg, idx) => {
      const segType = splitElements(seg, sep)[0];
      if (!segmentMap[segType]) segmentMap[segType] = [];
      segmentMap[segType].push({ line: idx + 1, content: seg });
    });

    // V-001: Mindestens UNB, UNZ, UNH, UNT vorhanden
    const hasUNB = segmentMap['UNB']?.length > 0;
    const hasUNZ = segmentMap['UNZ']?.length > 0;
    const hasUNH = segmentMap['UNH']?.length > 0;
    const hasUNT = segmentMap['UNT']?.length > 0;

    if (!hasUNB || !hasUNZ || !hasUNH || !hasUNT) {
      if (!hasUNB) errors.push({
        type: 'error',
        title: 'V-001: UNB fehlt',
        segment: 'UNB',
        line: 1,
        message: 'Interchange Header (UNB) ist erforderlich.',
        tip: 'Jede EDIFACT-Datei muss mit einem UNB-Segment beginnen.'
      });
      if (!hasUNZ) errors.push({
        type: 'error',
        title: 'V-001: UNZ fehlt',
        segment: 'UNZ',
        line: segments.length,
        message: 'Interchange Ende (UNZ) ist erforderlich.',
        tip: 'Jede EDIFACT-Datei muss mit einem UNZ-Segment enden.'
      });
      if (!hasUNH) errors.push({
        type: 'error',
        title: 'V-001: UNH fehlt',
        segment: 'UNH',
        line: 2,
        message: 'Nachrichtenkopf (UNH) ist erforderlich.',
        tip: 'Jede Nachricht benötigt ein UNH-Segment.'
      });
      if (!hasUNT) errors.push({
        type: 'error',
        title: 'V-001: UNT fehlt',
        segment: 'UNT',
        line: segments.length - 1,
        message: 'Nachrichtenende (UNT) ist erforderlich.',
        tip: 'Jede Nachricht muss mit einem UNT-Segment enden.'
      });
    }

    // V-002: UNB/UNZ müssen als Paar vorkommen
    const unbCount = segmentMap['UNB']?.length || 0;
    const unzCount = segmentMap['UNZ']?.length || 0;
    
    if (unbCount !== 1 || unzCount !== 1) {
      errors.push({
        type: 'error',
        title: 'V-002: UNB/UNZ Paar-Fehler',
        segment: 'UNB/UNZ',
        line: 1,
        message: `Erwartet: genau 1 UNB und 1 UNZ, gefunden: ${unbCount} UNB, ${unzCount} UNZ`,
        tip: 'Jede EDIFACT-Datei braucht genau ein UNB/UNZ-Paar.'
      });
    }

    // Prüfe ob UNB vor UNZ kommt
    if (hasUNB && hasUNZ) {
      const unbLine = segmentMap['UNB'][0].line;
      const unzLine = segmentMap['UNZ'][0].line;
      if (unzLine < unbLine) {
        errors.push({
          type: 'error',
          title: 'V-002: UNZ vor UNB',
          segment: 'UNZ',
          line: unzLine,
          message: 'UNZ darf nicht vor UNB stehen.',
          tip: 'Die Reihenfolge muss sein: UNB ... UNZ'
        });
      }
    }

    // V-003: UNH/UNT müssen paarweise vorkommen
    const unhCount = segmentMap['UNH']?.length || 0;
    const untCount = segmentMap['UNT']?.length || 0;
    
    if (unhCount !== untCount) {
      errors.push({
        type: 'error',
        title: 'V-003: UNH/UNT Paar-Fehler',
        segment: 'UNH/UNT',
        line: 2,
        message: `Anzahl stimmt nicht überein: ${unhCount} UNH, ${untCount} UNT`,
        tip: 'Jedes UNH braucht ein zugehöriges UNT.'
      });
    }

    // V-003: UNT muss nach UNH kommen
    if (segmentMap['UNH'] && segmentMap['UNT']) {
      segmentMap['UNH'].forEach((unh, idx) => {
        const unt = segmentMap['UNT'][idx];
        if (unt && unt.line < unh.line) {
          errors.push({
            type: 'error',
            title: 'V-003: UNT vor UNH',
            segment: 'UNT',
            line: unt.line,
            message: `UNT (Zeile ${unt.line}) steht vor zugehörigem UNH (Zeile ${unh.line})`,
            tip: 'Die Reihenfolge muss sein: UNH ... UNT'
          });
        }
      });
    }

    // V-004: UNT-Segmentzähler prüfen & V-006: BGM vorhanden
    let currentMessage = null;
    let segmentCount = 0;
    let currentGroup = null; // UNG … UNE
    
    segments.forEach((seg, idx) => {
      const line = idx + 1;
      
      // UNA Spezialbehandlung - überspringen
      if (sep.una && seg === sep.una) {
        return; // UNA nicht weiter verarbeiten
      }
      
      const parts = splitElements(seg, sep);
      const segType = parts[0];

      // V-014: Funktionsgruppe – Anzahl Nachrichten und Referenz im UNE prüfen
      if (segType === 'UNG') {
        currentGroup = { line, reference: parts[5] || '', messages: 0 };
      }
      if (segType === 'UNE') {
        if (!currentGroup) {
          errors.push({
            type: 'error', title: 'V-014: UNE ohne UNG', segment: 'UNE', line,
            message: 'UNE-Segment ohne vorheriges UNG gefunden.',
            tip: 'Eine Funktionsgruppe beginnt mit UNG und endet mit UNE.'
          });
        } else {
          const declared = parseInt(parts[1], 10);
          if (!isNaN(declared) && declared !== currentGroup.messages) {
            errors.push({
              type: 'error', title: 'V-014: UNE-Nachrichtenzähler falsch', segment: 'UNE', line,
              message: `UNE meldet ${declared} Nachricht(en), gefunden: ${currentGroup.messages}`,
              tip: 'UNE+<Anzahl Nachrichten in der Gruppe>+<Referenz>'
            });
          }
          if ((parts[2] || '') !== currentGroup.reference) {
            errors.push({
              type: 'error', title: 'V-014: UNE-Referenz passt nicht zu UNG', segment: 'UNE', line,
              message: `UNE-Referenz "${parts[2] || ''}" ≠ UNG-Referenz "${currentGroup.reference}" (Zeile ${currentGroup.line})`,
              tip: 'Die Referenz im UNE muss der Gruppenreferenz im UNG (5. Datenelement) entsprechen.'
            });
          }
          currentGroup = null;
        }
      }

      // UNH gefunden - neue Nachricht beginnt
      if (segType === 'UNH') {
        if (currentGroup) currentGroup.messages++;
        currentMessage = { 
          unhLine: line, 
          reference: parts[1] || '?', 
          type: splitComponents(parts[2] || '', sep)[0],
          segmentCount: 0,
          hasBGM: false,
          bgmLine: null
        };
        segmentCount = 0;
      }

      // Segmente innerhalb einer Nachricht zählen
      if (currentMessage !== null) {
        segmentCount++;
        
        // V-006: BGM prüfen
        if (segType === 'BGM') {
          currentMessage.hasBGM = true;
          currentMessage.bgmLine = line;
        }
      }

      // V-004: UNT gefunden - Segmentzählung prüfen
      if (segType === 'UNT') {
        if (currentMessage === null) {
          errors.push({
            type: 'error',
            title: 'V-003: UNT ohne UNH',
            segment: 'UNT',
            line: line,
            message: 'UNT-Segment ohne zugehöriges UNH gefunden.',
            tip: 'Jedes UNT muss ein vorheriges UNH haben.'
          });
        } else {
          const declaredCount = parseInt(parts[1], 10);
          if (isNaN(declaredCount)) {
            errors.push({
              type: 'error',
              title: 'V-004: UNT-Zähler ungültig',
              segment: 'UNT',
              line: line,
              message: `Segmentanzahl ist nicht numerisch: "${parts[1]}"`,
              tip: 'UNT+<Anzahl>+<Referenz> - Anzahl muss eine Zahl sein.'
            });
          } else if (declaredCount !== segmentCount) {
            errors.push({
              type: 'error',
              title: 'V-004: UNT-Segmentanzahl falsch',
              segment: 'UNT',
              line: line,
              message: `Erwartet: ${declaredCount} Segmente, gefunden: ${segmentCount}`,
              tip: 'Zähle alle Segmente von UNH bis UNT (inkl. beide).'
            });
          }
          
          // V-012: UNT-Referenz muss der UNH-Referenz entsprechen
          if ((parts[2] || '') !== currentMessage.reference) {
            errors.push({
              type: 'error',
              title: 'V-012: UNT-Referenz passt nicht zu UNH',
              segment: 'UNT',
              line: line,
              message: `UNT-Referenz "${parts[2] || ''}" ≠ UNH-Referenz "${currentMessage.reference}" (Zeile ${currentMessage.unhLine})`,
              tip: 'UNT+<Anzahl>+<Referenz> – die Referenz muss genau der Nachrichtenreferenz im UNH entsprechen.'
            });
          }

          // V-006: BGM-Prüfung (nicht bei Service-Nachrichten wie CONTRL)
          if (!currentMessage.hasBGM && !NO_BGM_MESSAGES.includes(currentMessage.type)) {
            errors.push({
              type: 'error',
              title: 'V-006: BGM fehlt',
              segment: 'BGM',
              line: currentMessage.unhLine,
              message: `Nachricht (UNH Zeile ${currentMessage.unhLine}) hat kein BGM-Segment.`,
              tip: 'Jede Nachricht muss einen Belegkopf (BGM) enthalten.'
            });
          }
          
          currentMessage = null;
        }
      }

      // V-007: Segmentname validieren (UNA überspringen - Spezialfall)
      if (segType && segType !== 'UNA') {
        if (segType.length === 0) {
          errors.push({
            type: 'error',
            title: 'V-007: Leerer Segmentname',
            segment: '-',
            line: line,
            message: 'Segment-Tag ist leer.',
            tip: 'Jedes Segment muss einen gültigen Namen haben (z.B. UNB, BGM).'
          });
        } else if (!/^[A-Z]{2,6}$/i.test(segType)) {
          errors.push({
            type: 'error',
            title: 'V-007: Ungültiger Segmentname',
            segment: segType,
            line: line,
            message: `"${segType}" ist kein gültiger Segment-Tag.`,
            tip: 'Segment-Tags müssen 2-6 Buchstaben sein (z.B. UNB, BGM, DTM).'
          });
        } else {
          // Prüfe ob Segment bekannt ist
          if (!KNOWN_SEGMENTS.has(segType.toUpperCase()) && segType.length === 3) {
            warnings.push({
              type: 'warning',
              title: 'V-007: Unbekanntes Segment',
              segment: segType,
              line: line,
              message: `"${segType}" ist nicht im EDIFACT-Standard bekannt.`,
              tip: 'Möglicherweise Tippfehler oder kundenspezifische Erweiterung.'
            });
          }
        }
      }

      // V-008: Element-Struktur prüfen
      if (parts.length === 1 && segType !== 'UNA') {
        // Segment hat keine Elemente nach dem Tag
        const requiresElements = ['UNB', 'UNH', 'UNT', 'UNZ', 'BGM', 'DTM', 'NAD'];
        if (requiresElements.includes(segType)) {
          errors.push({
            type: 'error',
            title: 'V-008: Fehlende Segment-Elemente',
            segment: segType,
            line: line,
            message: `${segType} benötigt Daten-Elemente.`,
            tip: `Format: ${segType}+Element1+Element2+...`
          });
        }
      }

      // V-009: DTM-Format prüfen
      if (segType === 'DTM' && parts.length > 1) {
        const dtmData = splitComponents(parts[1], sep);
        const qualifier = dtmData[0];
        const dateValue = dtmData[1];
        const formatCode = dtmData[2];
        
        if (dateValue) {
          if (formatCode === '102') {
            // YYYYMMDD
            if (!/^\d{8}$/.test(dateValue)) {
              warnings.push({
                type: 'warning',
                title: 'V-009: DTM-Format falsch',
                segment: 'DTM',
                line: line,
                message: `Datum "${dateValue}" muss Format YYYYMMDD haben (8 Ziffern).`,
                expected: 'YYYYMMDD (z.B. 20250105)',
                tip: 'Bei Formatcode 102 wird YYYYMMDD erwartet.'
              });
            }
          } else if (formatCode === '203') {
            // YYYYMMDDHHMM
            if (!/^\d{12}$/.test(dateValue)) {
              warnings.push({
                type: 'warning',
                title: 'V-009: DTM-Format falsch',
                segment: 'DTM',
                line: line,
                message: `Datum "${dateValue}" muss Format YYYYMMDDHHMM haben (12 Ziffern).`,
                expected: 'YYYYMMDDHHMM (z.B. 202501051430)',
                tip: 'Bei Formatcode 203 wird YYYYMMDDHHMM erwartet.'
              });
            }
          }
        }
      }

      // V-010: NAD mit Partei-ID prüfen
      if (segType === 'NAD' && parts.length > 2) {
        const qualifier = parts[1]; // BY, SU, etc.
        const idField = parts[2]; // z.B. 1234567890::9
        
        if (['BY', 'SU', 'DP', 'IV'].includes(qualifier)) {
          if (!idField || !splitComponents(idField, sep)[0]) {
            warnings.push({
              type: 'warning',
              title: 'V-010: NAD ohne Partei-ID',
              segment: 'NAD',
              line: line,
              message: `NAD+${qualifier} hat keine gültige Partei-ID.`,
              tip: 'Format: NAD+BY+1234567890::9 (GLN erforderlich)'
            });
          }
        }
      }

      // V-103: MOA-Beträge prüfen
      if (segType === 'MOA' && parts.length > 1) {
        const moaData = splitComponents(parts[1], sep);
        const amount = moaData[1];
        if (amount && !NUMBER.test(amount)) {
          warnings.push({
            type: 'warning',
            title: 'V-103: MOA-Betrag ungültig',
            segment: 'MOA',
            line: line,
            message: `Betrag "${amount}" ist nicht numerisch.`,
            tip: 'Beträge müssen numerisch sein mit Punkt als Dezimaltrennzeichen (z.B. 199.90).'
          });
        }
      }

      // V-104: PRI-Preise prüfen
      if (segType === 'PRI' && parts.length > 1) {
        const priData = splitComponents(parts[1], sep);
        const price = priData[1];
        if (price && !NUMBER.test(price)) {
          warnings.push({
            type: 'warning',
            title: 'V-104: PRI-Preis ungültig',
            segment: 'PRI',
            line: line,
            message: `Preis "${price}" ist nicht numerisch.`,
            tip: 'Preise müssen numerisch sein mit Punkt als Dezimaltrennzeichen (z.B. 19.99).'
          });
        }
      }
    });

    // V-005: UNZ-Nachrichtenzähler prüfen
    if (segmentMap['UNZ']?.length > 0) {
      const unzSeg = segmentMap['UNZ'][0].content;
      const unzParts = splitElements(unzSeg, sep);
      const declaredMsgCount = parseInt(unzParts[1], 10);
      const ungCount = segmentMap['UNG']?.length || 0;
      const actualMsgCount = ungCount > 0 ? ungCount : unhCount;

      // V-013: UNZ-Referenz muss der UNB-Datenaustauschreferenz entsprechen
      if (segmentMap['UNB']?.length > 0) {
        const unbRef = splitElements(segmentMap['UNB'][0].content, sep)[5] || '';
        if ((unzParts[2] || '') !== unbRef) {
          errors.push({
            type: 'error',
            title: 'V-013: UNZ-Referenz passt nicht zu UNB',
            segment: 'UNZ',
            line: segmentMap['UNZ'][0].line,
            message: `UNZ-Referenz "${unzParts[2] || ''}" ≠ UNB-Referenz "${unbRef}"`,
            tip: 'UNZ+<Anzahl>+<Referenz> – die Referenz muss der Datenaustauschreferenz im UNB (5. Datenelement) entsprechen.'
          });
        }
      }
      
      if (!isNaN(declaredMsgCount) && declaredMsgCount !== actualMsgCount) {
        errors.push({
          type: 'error',
          title: 'V-005: UNZ-Nachrichtenzähler falsch',
          segment: 'UNZ',
          line: segmentMap['UNZ'][0].line,
          message: ungCount > 0
            ? `UNZ meldet ${declaredMsgCount} Funktionsgruppe(n), gefunden: ${actualMsgCount}`
            : `UNZ meldet ${declaredMsgCount} Nachricht(en), gefunden: ${actualMsgCount}`,
          tip: 'UNZ+<Anzahl Nachrichten>+<Referenz> - Anzahl muss stimmen.'
        });
      }
    }

    // V-102: CNT-Prüfung (LIN-Anzahl)
    if (segmentMap['CNT'] && segmentMap['LIN']) {
      segmentMap['CNT'].forEach(cnt => {
        const parts = splitElements(cnt.content, sep);
        const cntData = splitComponents(parts[1] || '', sep);
        if (cntData[0] === '2') {
          const declaredLineCount = parseInt(cntData[1], 10);
          const actualLineCount = segmentMap['LIN'].length;
          
          if (!isNaN(declaredLineCount) && declaredLineCount !== actualLineCount) {
            warnings.push({
              type: 'warning',
              title: 'V-102: CNT-Positionsanzahl falsch',
              segment: 'CNT',
              line: cnt.line,
              message: `CNT meldet ${declaredLineCount} Positionen, gefunden: ${actualLineCount} LIN-Segmente`,
              tip: 'Prüfe ob alle LIN-Segmente vorhanden sind.'
            });
          }
        }
      });
    }

    const isValid = errors.length === 0;
    return { errors, warnings, infos, isValid };
  };

  const loadToken = useRef(0); // verhindert, dass ein älterer Ladevorgang ein neueres Ergebnis überschreibt

  const processFile = async (file) => {
    if (!file) return;
    const token = ++loadToken.current;

    // Größe, Zeichensatz (UTF-8 oder Latin-1) und EDIFACT-Struktur prüfen
    const { text, error } = await readEdifactFile(file, MAX_BYTES);
    if (token !== loadToken.current) return;
    if (error) {
      setError(error);
      setValidationResult(null);
      return;
    }

    try {
      setEdifactText(text);
      setValidationResult(validateEdifact(text));
      setError("");
      setLoadedFile({ name: file.name });
    } catch (err) {
      console.error('Error processing file:', err);
      setError("❌ Fehler beim Verarbeiten der Datei.");
      setValidationResult(null);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    
    // Prüfen ob Datenschutz bestätigt wurde
    if (!privacyAccepted) {
      setPendingFile(file);
      setShowPrivacyPopup(true);
      return;
    }
    
    // Datenschutz ist akzeptiert, Datei direkt verarbeiten
    processFile(file);
  };

  const handlePrivacyAccept = () => {
    setPrivacyAccepted(true);
    setShowPrivacyPopup(false);
    if (pendingFile) {
      processFile(pendingFile);
      setPendingFile(null);
    }
  };

  const handleErrorClick = (issue) => {
    setHighlightedSegment(issue.line);
    // Scroll zu dem Segment im Text
    setTimeout(() => {
      const element = document.getElementById(`segment-line-${issue.line}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const renderEdifactText = () => {
    if (!edifactText) return null;

    const sep = getSeparators(edifactText);
    const segments = splitSegments(edifactText, sep);
    const shown = segments.length > CODE_VIEW_LIMIT ? segments.slice(0, CODE_VIEW_LIMIT) : segments;

    return (
      <div className="code-block code-lines" style={{ maxHeight: 520 }}>
        {shown.map((seg, idx) => {
          const line = idx + 1;
          const isHighlighted = highlightedSegment === line;

          return (
            <div
              key={idx}
              id={`segment-line-${line}`}
              className={"code-line" + (isHighlighted ? " hl" : "")}
            >
              <span className="ln">{String(line).padStart(3, '0')}</span>
              <span>{sep.una && seg === sep.una ? seg : seg + sep.segment}</span>
            </div>
          );
        })}
        {segments.length > shown.length && (
          <div className="code-line">
            <span className="ln">…</span>
            <span>{(segments.length - shown.length).toLocaleString("de-DE")} weitere Segmente – geprüft wurden alle, angezeigt werden die ersten {CODE_VIEW_LIMIT.toLocaleString("de-DE")}.</span>
          </div>
        )}
      </div>
    );
  };

  const renderValidationSummary = () => {
    if (!validationResult) return null;

    const { errors, warnings, infos } = validationResult;

    let status = 'ok';
    let StatusIcon = LuCircleCheck;
    let summaryText = 'Keine Fehler gefunden – Syntax scheint korrekt.';

    if (errors.length > 0) {
      status = 'fail';
      StatusIcon = LuCircleX;
      summaryText = `${errors.length} Fehler gefunden – Datei scheint ungültig.`;
    } else if (warnings.length > 0) {
      status = 'warn';
      StatusIcon = LuTriangleAlert;
      summaryText = `Keine Fehler, aber ${warnings.length} Warnung${warnings.length > 1 ? 'en' : ''} gefunden.`;
    }

    return (
      <div className={`status-banner ${status}`} role="status">
        <span className="status-icon"><StatusIcon size={24} aria-hidden="true" /></span>
        <div>
          <div className="status-title">{summaryText}</div>
          <div className="status-meta">
            <span className="badge badge-danger">{errors.length} Fehler</span>
            <span className="badge badge-warning">{warnings.length} Warnung{warnings.length !== 1 ? 'en' : ''}</span>
            <span className="badge">{infos.length} Hinweis{infos.length !== 1 ? 'e' : ''}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderIssueCard = (issue, idx) => {
    const icons = {
      error: LuCircleX,
      warning: LuTriangleAlert,
      info: LuInfo
    };
    const Icon = icons[issue.type] || LuInfo;

    return (
      <button
        type="button"
        key={idx}
        className={`issue ${issue.type}`}
        onClick={() => handleErrorClick(issue)}
      >
        <Icon className="issue-icon" size={18} aria-hidden="true" />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="issue-title">{issue.title}</span>
          <span className="issue-meta">
            Segment: <strong>{issue.segment}</strong> · Zeile {issue.line}
          </span>
          <span className="issue-text">
            {issue.message}
            {issue.expected && (
              <span style={{ display: 'block', marginTop: 4 }}>
                Erwartet: <strong>{issue.expected}</strong>
              </span>
            )}
          </span>
          {issue.tip && (
            <span className="issue-tip">
              <LuLightbulb size={14} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 6, color: '#d97706' }} />
              <strong>Tipp:</strong> {issue.tip}
            </span>
          )}
        </span>
      </button>
    );
  };

  const renderIssueList = () => {
    if (!validationResult) return null;

    const { errors, warnings, infos } = validationResult;
    const allIssues = [...errors, ...warnings, ...infos];

    if (allIssues.length === 0) {
      return (
        <div className="alert alert-success">
          <LuCircleCheck size={20} aria-hidden="true" />
          <div>
            <div className="alert-title">Keine Probleme gefunden!</div>
            <p style={{ marginTop: 4 }}>
              Hinweis: Die businesslogische Prüfung (z. B. ob Preise stimmen) ist nicht Teil dieser Validierung.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div>
        <h2 className="h3" style={{ marginBottom: 14 }}>Fehlersuche</h2>
        <div style={{ display: 'grid', gap: 10 }}>
          {allIssues.map((issue, idx) => renderIssueCard(issue, idx))}
        </div>
      </div>
    );
  };

  const hasIssues = validationResult && (validationResult.errors.length > 0 || validationResult.warnings.length > 0);

  return (
    <>
      <SeoHead
        {...pageMeta["/edifact-validator"]}
        keywords="EDIFACT Validator, EDI Validator, EDIFACT Fehler, EDI Syntax Check, EDIFACT prüfen, EDI Validierung"
        ogUrl="https://edi-converter.de/edifact-validator"
      />

      <ToolHero
        tool="validate"
        title="EDIFACT Validator"
        description="Prüfe deine EDIFACT-Datei auf Syntax, Struktur & typische Fehler – direkt im Browser."
        intro="EDIFACT-Datei hier ablegen und sofort auf Fehler prüfen."
      >
        <Dropzone
          onFile={handleFile}
          inputRef={fileInputRef}
          title="EDIFACT-Datei hochladen"
          titleAs="p"
          hint="Ziehe eine .edi oder .txt Datei hierher oder klicke zum Auswählen"
          loadedName={validationResult ? loadedFile?.name : ""}
        />

        {error && (
          <div className="alert alert-danger" role="alert" style={{ marginTop: 16 }}>
            <LuCircleAlert size={20} aria-hidden="true" />
            <span>{error.replace(/^❌\s*/, "")}</span>
          </div>
        )}
      </ToolHero>

      {/* Validation Results */}
      {validationResult && (
        <section className="result-section" style={{ paddingTop: 48 }}>
          <div className="container stack-lg" ref={resultRef}>
            {renderValidationSummary()}

            <div className={hasIssues ? "grid grid-2" : ""} style={{ alignItems: "start" }}>
              {/* Fehlerliste */}
              <div>
                {renderIssueList()}

                {hasIssues && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    style={{ marginTop: 20 }}
                    onClick={() => {
                      setValidationResult(null);
                      setEdifactText("");
                      setHighlightedSegment(null);
                    }}
                  >
                    <LuRotateCcw size={16} aria-hidden="true" /> Datei erneut prüfen
                  </button>
                )}
              </div>

              {/* EDIFACT Rohansicht */}
              {hasIssues && (
                <div style={{ minWidth: 0 }}>
                  <h2 className="h3" style={{ marginBottom: 14 }}>EDIFACT-Rohansicht</h2>
                  {renderEdifactText()}
                  <p className="small muted" style={{ marginTop: 10 }}>
                    Klicke auf einen Fehler in der Liste, um die Stelle im Text hervorzuheben.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {showPrivacyPopup && (
        <PrivacyPopup
          show={showPrivacyPopup}
          onAccept={handlePrivacyAccept}
          onCancel={() => {
            setShowPrivacyPopup(false);
            setPendingFile(null);
          }}
        />
      )}
    </>
  );
}
